import { useState } from "react";
import { markNoReplyNeeded } from "../services/api";

const WAITING_REPLY = "Patient texted, waiting for your reply";

// The reasons this lead needs staff now, most urgent first: waiting reply,
// needs callback, past-due follow-up, follow-up due today. Rules only; the AI
// next action is advice and is not a reason.
const leadReasons = (lead) => {
  const reasons = [];
  if (lead.waiting_reply) {
    reasons.push(WAITING_REPLY);
  }
  // From the server, built on the Needs Callback worklist filter, so the lead
  // and the worklist agree. Do not compute it here.
  if (lead.needs_callback) {
    reasons.push(
      lead.call_outcome === "message_left"
        ? "Caller left a message, call them back"
        : "Call not answered, call them back",
    );
  }
  if (lead.follow_up_date) {
    // Compare as plain dates: follow_up_date is date-only.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(`${lead.follow_up_date}T00:00:00`);
    if (due < today) reasons.push("Follow-up past due");
    else if (due.getTime() === today.getTime()) reasons.push("Follow-up due today");
  }
  return reasons;
};

// One reason line at the top of a lead, shared by the side pane and the full
// lead page. Absent when no reason applies. A waiting reply offers "No reply
// needed"; the server returns the updated lead, passed to onLeadUpdate.
// `embedded` draws it as a tinted band inside a parent card, with no box.
const ReasonLine = ({ lead, onLeadUpdate, className = "", embedded = false }) => {
  const [saving, setSaving] = useState(false);
  const [main, ...others] = leadReasons(lead);
  if (!main) return null;

  const handleNoReplyNeeded = async () => {
    setSaving(true);
    try {
      const updated = await markNoReplyNeeded(lead.id);
      onLeadUpdate?.(updated);
    } catch (err) {
      console.error("Error marking no reply needed:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`px-4 py-2.5 bg-amber-50 ${
        embedded ? "border-t border-border" : "border border-amber-200 rounded-lg"
      } ${className}`}
    >
      <div className="flex items-center justify-between gap-3">
        <p data-testid="reason-main" className="text-sm font-medium text-amber-900">
          {main}
        </p>
        {main === WAITING_REPLY && (
          <button
            type="button"
            onClick={handleNoReplyNeeded}
            disabled={saving}
            className="shrink-0 text-xs font-medium text-amber-900 underline hover:text-amber-700 disabled:opacity-50"
          >
            No reply needed
          </button>
        )}
      </div>
      {others.map((reason) => (
        <p key={reason} data-testid="reason-other" className="text-xs text-amber-800">
          {reason}
        </p>
      ))}
    </div>
  );
};

export default ReasonLine;
